import { type FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { useSelector } from "@jti/store/reduxHooks";
import { selectProductsInCart } from "@jti/store/selectors/selectProductsInCart";
import { LoyaltyReferralCode } from "@jti/ui";

import { CheckoutLoyaltyPoints } from "@checkout/components/CheckoutLoyaltyPoints";
import { PromoCode } from "@checkout/components/PromoCode";
import { SummaryCartItems } from "@checkout/components/SummaryCartItems";
import { SummarySubscription } from "@checkout/components/SummarySubscription";
import type { CheckOutSteps } from "@checkout/constants";
import { useSubscription } from "@checkout/hooks";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import { useLoyaltyStatus } from "@checkout/hooks/useLoyaltyStatus";
import { AsideLayout } from "@checkout/layouts/AsideLayout";
import { categorizeItems } from "@checkout/utils/categorizeItems";

import { CartPaymentSummary } from "../CartPaymentSummary";

import "../CheckoutLoyaltyPoints/CheckoutLoyaltyPoints.scss";
import styles from "./CheckoutAside.module.scss";

type CheckoutAsideProps = {
  submitButtonWrapperRef: React.RefObject<HTMLDivElement | null>;
  step: CheckOutSteps;
};

export const CheckoutAside: FC<CheckoutAsideProps> = ({ submitButtonWrapperRef, step }) => {
  const checkoutConfiguration = useCheckoutConfiguration();
  const { loyalty } = checkoutConfiguration;
  const { isFreeTrial } = useFreeTrial();
  const hasLoyalty = useLoyaltyStatus();
  const { isSubscription } = useSubscription();
  const { showPricesWithTaxExcluded = false } = useConfig();

  const { promoCode, itemsPending } = useSelector((store) => store.cart);
  const productsInCart = useSelector((state) => selectProductsInCart(state, isFreeTrial, showPricesWithTaxExcluded));
  const { subscriptionItems } = categorizeItems(productsInCart);

  const showLoyaltyBlock = hasLoyalty && !isFreeTrial && !isSubscription;

  const isPromoCodeVisible = !isFreeTrial && productsInCart.length && promoCode.isVisible;

  const onReferralCodeApplied = () => {
    window.location.href = window.location.href.split("#")[0];
  };

  return (
    <>
      <AsideLayout
        data-testid="regularCartSummaryBlock"
        referralCodeSlot={
          !isFreeTrial && loyalty.showMgm ? (
            <LoyaltyReferralCode onCodeApplied={onReferralCodeApplied} source="checkout" />
          ) : null
        }
        slot3={<SummaryCartItems items={productsInCart} isLoading={itemsPending} />}
        slot5={<SummarySubscription items={subscriptionItems} />}
        slot6={
          <>
            {isPromoCodeVisible ? <PromoCode step={step} /> : null}
            {showLoyaltyBlock && <CheckoutLoyaltyPoints />}
          </>
        }
        paymentSummarySlot={<CartPaymentSummary showLoyaltyPointsSummary />}
      />
      <div className={styles.asideButton} data-testid="aside-submit-button-wrapper" ref={submitButtonWrapperRef} />
    </>
  );
};
